// Companion/blocks/chatBlockTargets.ts
//
// Action outcomes and stored navigation targets carry a `ChatNavigationTarget`:
// a *named route id* plus params (ora-be ADR-0006). This is the only module that turns
// one into a URL.
//
// The vocabulary is `ChatRouteId` — the same ids routes publish as
// `handle.chatRoute` for the app-context envelope (ORA-449) — so the companion
// has one name per surface whether it is reading where the student is or
// sending them somewhere. A route id this resolver does not know, or one whose
// required param is missing, yields no destination: an unresolvable target must
// never navigate a student somewhere arbitrary, and the model can never hand
// out a raw path.

import { CHAT_ROUTE_IDS, type ChatRouteId } from '@/types/chatEnvelope';
import type { ChatCitation, ChatNavigationTarget } from '@/types/chatStream';
import type { Region } from '@/types/documentModel';

export const isPositiveSafeInteger = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const positiveInteger = (value: string | undefined): number | null => {
  if (!value || !/^\d+$/.test(value)) return null;
  const parsed = Number(value);
  return isPositiveSafeInteger(parsed) ? parsed : null;
};

/**
 * A study path, carrying whichever of the block and the study set the target
 * names. A follow-up may point at the runner with no block ("continue your
 * questions"), so the bare path stays valid; a created block always names
 * itself, and a set-driven run (ORA-677) names its set so the runner can load
 * the set's items and report the student back to the thread.
 */
const withStudyParams = (
  path: string,
  params: { blockId?: string; setId?: string }
): string => {
  // Encoded with `encodeURIComponent` rather than `URLSearchParams`, which
  // writes a space as `+`; these ids end up in a loader's `searchParams.get`,
  // and percent-encoding is what the block routes have always produced.
  const query = [
    params.blockId ? `blockId=${encodeURIComponent(params.blockId)}` : null,
    params.setId ? `setId=${encodeURIComponent(params.setId)}` : null,
  ].filter((part): part is string => part !== null);

  return query.length > 0 ? `${path}?${query.join('&')}` : path;
};

/**
 * Route ids that open a specific QBank block outside the Today session. A
 * target naming one of these with a `blockId` must enter standalone study
 * before navigating (ORA-674): outside standalone mode the block loader ignores
 * the id and loads the daily session's active block instead.
 */
const STANDALONE_BLOCK_ROUTES: ReadonlySet<string> = new Set<ChatRouteId>([
  'study.qbank.block',
  'study.qbank.review',
]);

/**
 * Route ids a block or follow-up may be sent to, and how each becomes a path.
 * Deliberately a subset of `ChatRouteId`: a live self-assessment, the webinar
 * console and `other` are not places the companion may drop a student.
 */
const TARGET_PATHS: Partial<
  Record<ChatRouteId, (params: Record<string, string>) => string | null>
> = {
  dashboard: () => '/',
  performance: () => '/performance',
  chat: () => '/chat',
  topics: () => '/topics',
  library: () => '/library',
  videos: ({ videoId }) => (videoId ? `/videos/${videoId}` : '/videos'),
  'audio-overviews': () => '/audio-overviews',
  article: ({ articleId }) =>
    articleId ? `/library/${encodeURIComponent(articleId)}` : null,
  document: ({ lectureId, page }) => {
    if (!lectureId) return null;
    const path = `/document/${encodeURIComponent(lectureId)}`;
    const parsedPage = positiveInteger(page);
    return parsedPage ? `${path}?page=${parsedPage}` : path;
  },
  help: ({ slug }) => (slug ? `/help/${slug}` : '/help'),
  'flashcard-decks': () => '/flashcard-decks',
  'self-assessments': () => '/self-assessments',
  'qbank.start': () => '/qbank-start',
  'qbank.today_session': () => '/?open=qbank_today_session',
  'study.qbank.block': ({ blockId, setId }) =>
    withStudyParams('/study/qbank/block', { blockId, setId }),
  'study.qbank.review': ({ blockId, setId }) =>
    withStudyParams('/study/qbank/block/review', { blockId, setId }),
  'study.flashcards': ({ setId }) =>
    withStudyParams('/study/flashcards', { setId }),
  'study.video': ({ setId }) => withStudyParams('/study/video', { setId }),
  'study.summary': () => '/study/summary',
  games: () => '/games',
  account: ({ sub, scrollTo }) => {
    const params = new URLSearchParams();
    if (sub && ['profile', 'security', 'settings', 'billing'].includes(sub)) {
      params.set('sub', sub);
    }
    if (scrollTo && sub === 'settings') params.set('scrollTo', scrollTo);
    const query = params.toString();
    return query ? `/account?${query}` : '/account';
  },
};

/** Every route id a target may name. Exported so tests can assert the set. */
export const CHAT_BLOCK_TARGET_ROUTE_IDS = CHAT_ROUTE_IDS.filter(
  (id) => TARGET_PATHS[id] !== undefined
);

export interface ResolvedChatBlockTarget {
  /** The path to push. */
  to: string;
  /**
   * The QBank block this target opens on its own, outside the Today session.
   * Set only for the block runner / review routes with a `blockId`; the opener
   * enters standalone study for it before navigating. Pause or finish then
   * returns to the QBank page, where the block sits in Previous blocks.
   */
  standaloneBlockId: string | null;
}

const resolveTargetPath = (
  target: ChatNavigationTarget | null | undefined
): string | null => {
  if (!target) return null;

  const build = TARGET_PATHS[target.route as ChatRouteId];
  if (!build) return null;

  return build(target.params ?? {});
};

/**
 * Turn a target into somewhere to navigate, or null when it names a route this
 * client cannot open.
 *
 * Where the student came from is *not* carried here. It used to ride in the
 * location state, which no reader ever read and which a reload threw away;
 * the way back lives in the persisted study context instead (ORA-681).
 */
export const resolveChatBlockTarget = (
  target: ChatNavigationTarget | null | undefined
): ResolvedChatBlockTarget | null => {
  const to = resolveTargetPath(target);
  if (!to) return null;

  const blockId = target?.params?.blockId;

  const standaloneBlockId =
    target && STANDALONE_BLOCK_ROUTES.has(target.route) && blockId
      ? blockId
      : null;

  return { to, standaloneBlockId };
};

/**
 * The attachment page a `chat_document_page` citation opens, or null when it
 * names none. A citation read back from history may carry only its `target`
 * and `contentId` (the live stream also sets `documentId`), so the id falls
 * back to `target.params.documentId`, then `contentId`; the page to
 * `target.params.page`.
 */
export const resolveChatDocumentCitation = (
  citation: ChatCitation
): { documentId: string; page: number } | null => {
  if (citation.kind !== 'chat_document_page') return null;

  const params = citation.target?.params;

  const documentId =
    citation.documentId || params?.documentId || citation.contentId;

  const page = isPositiveSafeInteger(citation.page)
    ? citation.page
    : positiveInteger(params?.page);

  return documentId && page ? { documentId, page } : null;
};

/**
 * The lecture page a lecture citation opens beside the chat (ORA-736), or
 * null for a citation of anything else. A `document_page` citation names its
 * lecture directly; one read back from history may carry it only in its
 * `document` target. In the app the click opens the side viewer; the chip's
 * `/document/…` address remains for opening it in a new tab.
 */
export const resolveLectureCitation = (
  citation: ChatCitation
): {
  lectureId: string;
  page: number | null;
  factIds?: string[];
  regions?: Region[];
} | null => {
  const params =
    citation.target?.route === 'document' ? citation.target.params : undefined;

  if (citation.kind !== 'document_page' && !params) return null;

  const lectureId = citation.lectureId || params?.lectureId;

  const page = isPositiveSafeInteger(citation.page)
    ? citation.page
    : positiveInteger(params?.page);

  if (!lectureId) return null;

  // The document-model facts it cites and their boxes, for the viewer to
  // highlight; a citation without them opens the plain page as before.
  return {
    lectureId,
    page,
    ...(citation.factIds?.length ? { factIds: citation.factIds } : {}),
    ...(citation.regions?.length ? { regions: citation.regions } : {}),
  };
};

/**
 * Where a cited source opens, or null when it has nowhere honest to go.
 *
 * The server names the destination on the citation itself (ORA-605), as the
 * same closed route vocabulary blocks use — so a kind this client has never
 * heard of still opens, and a route id it does not know still opens nothing.
 * Citations written before that field existed carry no target, and the
 * document-page rule below is what keeps those chips working.
 */
export const resolveChatCitationTarget = (
  citation: ChatCitation
): string | null => {
  if (citation.kind === 'chat_document_page') {
    if (!resolveChatDocumentCitation(citation)) return null;

    // The ordinary click is intercepted by ChatConversationView. `/chat` is a
    // safe fallback for opening the chip in a new tab: like the `chat` route
    // id, it redirects to the AI chat (the desktop, or the sheet on phones).
    return '/chat';
  }

  if (citation.target) return resolveTargetPath(citation.target);

  if (citation.kind !== 'document_page') return null;
  if (!citation.lectureId || !isPositiveSafeInteger(citation.page)) {
    return null;
  }

  return resolveTargetPath({
    route: 'document',
    params: {
      lectureId: citation.lectureId,
      page: String(citation.page),
    },
  });
};
